import { Transition } from "@headlessui/react"
import { Form, Head, router, usePage } from "@inertiajs/react"
import { Trash2 } from "lucide-react"
import React from "react"

import DeleteUser from "@/components/delete-user"
import HeadingSmall from "@/components/heading-small"
import { ImagePicker } from "@/components/image-picker"
import InputError from "@/components/input-error"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Textarea } from "@/components/ui/textarea"
import AppLayout from "@/layouts/app-layout"
import SettingsLayout from "@/layouts/settings/layout"
import { settingsProfilePath } from "@/routes"
import type { BreadcrumbItem, SharedData } from "@/types"

const breadcrumbs: BreadcrumbItem[] = [
  {
    title: "Configuración de perfil",
    href: settingsProfilePath(),
  },
]

interface ProfileData {
  interest_ids?: number[]
  phone_number?: string | null
  phone_country_code?: string | null
  bio?: string | null
  origin_city?: string | null
  full_name?: string | null
}

export default function Profile() {
  const page = usePage<SharedData & { profile?: ProfileData }>()
  const { auth, profile } = page.props
  const cities = page.props.app?.cities ?? []
  const neighborhoods = page.props.app?.neighborhoods ?? []
  const interests = page.props.app?.interests ?? []
  const [selectedCityId, setSelectedCityId] = React.useState<
    number | undefined
  >(auth.user.current_city?.id)
  const [avatarPreview, setAvatarPreview] = React.useState<string | null>(null)

  React.useEffect(() => {
    return () => {
      if (avatarPreview) URL.revokeObjectURL(avatarPreview)
    }
  }, [avatarPreview])
  const filteredNeighborhoods = React.useMemo(() => {
    return neighborhoods.filter((n) =>
      selectedCityId ? n.city_id === selectedCityId : false,
    )
  }, [neighborhoods, selectedCityId])

  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head title={breadcrumbs[breadcrumbs.length - 1].title} />

      <SettingsLayout>
        <div className="space-y-6">
          <HeadingSmall
            title="Información del perfil"
            description="Actualiza tu perfil para que la comunidad te conozca mejor"
          />

          <Form
            method="patch"
            action={settingsProfilePath()}
            encType="multipart/form-data"
            options={{
              preserveScroll: true,
              preserveState: false,
            }}
            className="space-y-6"
          >
            {({ errors, processing, recentlySuccessful }) => (
              <>
                <div className="grid gap-6 md:grid-cols-2">
                  <div className="grid gap-2">
                    <Label htmlFor="current_city_id">Ciudad actual</Label>
                    <select
                      id="current_city_id"
                      name="current_city_id"
                      defaultValue={auth.user.current_city?.id ?? ""}
                      onChange={(e) =>
                        setSelectedCityId(
                          e.target.value ? Number(e.target.value) : undefined,
                        )
                      }
                      className="border-border bg-card text-foreground mt-1 block w-full rounded-md border px-3 py-2 text-sm focus:outline-none"
                    >
                      <option value="">Seleccionar ciudad</option>
                      {cities.map((c) => (
                        <option key={c.id} value={c.id}>
                          {c.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="grid gap-2">
                    <Label htmlFor="current_neighborhood_id">
                      Barrio actual
                    </Label>
                    <select
                      id="current_neighborhood_id"
                      name="current_neighborhood_id"
                      defaultValue={auth.user.neighborhood?.id ?? ""}
                      disabled={!selectedCityId}
                      className="border-border bg-card text-foreground mt-1 block w-full rounded-md border px-3 py-2 text-sm focus:outline-none disabled:cursor-not-allowed disabled:opacity-60"
                    >
                      <option value="">Seleccionar barrio</option>
                      {filteredNeighborhoods.map((n) => (
                        <option key={n.id} value={n.id}>
                          {n.name}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="grid gap-6 md:grid-cols-2">
                  <div className="grid gap-2">
                    <Label htmlFor="name">Nombre para mostrar</Label>
                    <Input
                      id="name"
                      name="name"
                      defaultValue={auth.user.name}
                      required
                      autoComplete="name"
                      placeholder="Nombre y apellido"
                    />
                    <InputError className="mt-1" message={errors.name} />
                  </div>

                  <div className="grid gap-2">
                    <Label htmlFor="username">Usuario</Label>
                    <Input
                      id="username"
                      name="username"
                      defaultValue={auth.user.username ?? ""}
                      placeholder="currar_123"
                    />
                    <InputError className="mt-1" message={errors.username} />
                  </div>
                </div>

                <div className="grid gap-6 md:grid-cols-2">
                  <div className="grid gap-2">
                    <Label>Imagen de perfil</Label>
                    <div className="flex items-center gap-4">
                      <ImagePicker
                        name="avatar"
                        onImageSelected={(file) => {
                          const url = URL.createObjectURL(file)
                          setAvatarPreview((prev) => {
                            if (prev) URL.revokeObjectURL(prev)
                            return url
                          })
                        }}
                        preview={avatarPreview ?? auth.user.avatar_url}
                        previewSize="sm"
                      />
                      {auth.user.avatar_url && !avatarPreview && (
                        <button
                          type="button"
                          onClick={() => {
                            if (
                              confirm(
                                "¿Estás seguro de que quieres eliminar tu foto de perfil?",
                              )
                            ) {
                              router.delete(settingsProfilePath() + "/avatar", {
                                preserveScroll: true,
                                onSuccess: () => {
                                  setAvatarPreview(null)
                                },
                              })
                            }
                          }}
                          className="rounded-full bg-red-500 p-1.5 text-white transition-colors hover:bg-red-600"
                          title="Eliminar foto"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      )}
                    </div>
                    <p className="text-muted-foreground text-xs">
                      Formatos admitidos: JPG, PNG. Tamaño recomendado cuadrado.
                    </p>
                  </div>

                  <div className="grid gap-2">
                    <Label>Intereses</Label>
                    <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
                      {interests.map((i) => {
                        const checked = (profile?.interest_ids ?? []).includes(
                          i.id,
                        )
                        return (
                          <label
                            key={i.id}
                            className="flex items-center gap-2 text-sm"
                          >
                            <input
                              type="checkbox"
                              name="interest_ids[]"
                              value={i.id}
                              defaultChecked={checked}
                              className="text-primary h-4 w-4 rounded border-neutral-300 focus:ring-0"
                            />
                            <span>{i.name}</span>
                          </label>
                        )
                      })}
                    </div>
                  </div>
                </div>

                <div className="grid gap-6 md:grid-cols-2">
                  <div className="grid gap-2">
                    <Label htmlFor="full_name">Nombre completo</Label>
                    <Input
                      id="full_name"
                      name="full_name"
                      defaultValue={profile?.full_name ?? ""}
                      placeholder="Nombre completo"
                    />
                    <InputError className="mt-1" message={errors.full_name} />
                  </div>

                  <div className="grid gap-2">
                    <Label htmlFor="origin_city">Ciudad de origen</Label>
                    <Input
                      id="origin_city"
                      name="origin_city"
                      defaultValue={profile?.origin_city ?? ""}
                      placeholder="Ej: Buenos Aires"
                    />
                    <InputError className="mt-1" message={errors.origin_city} />
                  </div>
                </div>

                <div className="grid gap-6 md:grid-cols-2">
                  <div className="grid gap-2">
                    <Label htmlFor="phone_country_code">Código país</Label>
                    <Input
                      id="phone_country_code"
                      name="phone_country_code"
                      defaultValue={profile?.phone_country_code ?? ""}
                      placeholder="+54"
                    />
                    <InputError
                      className="mt-1"
                      message={errors.phone_country_code}
                    />
                  </div>

                  <div className="grid gap-2">
                    <Label htmlFor="phone_number">Teléfono</Label>
                    <Input
                      id="phone_number"
                      name="phone_number"
                      defaultValue={profile?.phone_number ?? ""}
                      placeholder="11 5555 5555"
                    />
                    <InputError
                      className="mt-1"
                      message={errors.phone_number}
                    />
                  </div>
                </div>

                <div className="grid gap-2">
                  <Label htmlFor="bio">Bio</Label>
                  <Textarea
                    id="bio"
                    name="bio"
                    defaultValue={profile?.bio ?? ""}
                    placeholder="Contale a la comunidad quién sos y qué buscás en CurrAR"
                    rows={4}
                  />
                  <InputError className="mt-1" message={errors.bio} />
                </div>

                <div className="flex items-center gap-4">
                  <Button disabled={processing}>Guardar cambios</Button>

                  <Transition
                    show={recentlySuccessful}
                    enter="transition ease-in-out"
                    enterFrom="opacity-0"
                    leave="transition ease-in-out"
                    leaveTo="opacity-0"
                  >
                    <p className="text-muted-foreground text-sm">Guardado</p>
                  </Transition>
                </div>
              </>
            )}
          </Form>
        </div>

        <DeleteUser />
      </SettingsLayout>
    </AppLayout>
  )
}
